import { useState } from "react";
import { C, STAGES } from "../../../../constants";
import { daysAgo, fmtMoney } from "../../../../utils";

export default function PipelinePage({
  deals,
  setDeals,
  nav,
  KpiCardComponent,
  DealCatChipComponent,
  DealTypeBadgeComponent,
}) {
  const [search, setSearch] = useState("");
  const [ownerF, setOwnerF] = useState("All");
  const [trackView, setTrackView] = useState("both");
  const [probEdit, setProbEdit] = useState({});
  const owners = ["All", ...new Set(deals.map((deal) => deal.dealOwner))];

  const visible = deals.filter((deal) => {
    const ownerMatch = ownerF === "All" || deal.dealOwner === ownerF;
    const searchMatch = deal.companyName.toLowerCase().includes(search.toLowerCase()) || deal.opportunityName.toLowerCase().includes(search.toLowerCase());
    const trackMatch = trackView === "both" || (trackView === "new" && deal.category === "New Business") || (trackView === "existing" && deal.category === "Existing Client");
    return ownerMatch && searchMatch && trackMatch;
  });

  const totalVal = visible.reduce((sum, deal) => sum + deal.dealValue, 0);
  const weightedVal = visible.reduce((sum, deal) => sum + deal.dealValue * (deal.probability / 100), 0);
  const byCat = (category) => visible.filter((deal) => deal.category === category);

  function move(id, dir) {
    setDeals((state) => state.map((deal) => {
      if (deal.dealId !== id) return deal;
      const index = STAGES.indexOf(deal.stage);
      const nextIndex = Math.min(Math.max(index + dir, 0), STAGES.length - 1);
      return nextIndex === index ? deal : { ...deal, stage: STAGES[nextIndex], stageEnteredDate: "2026-03-24" };
    }));
  }

  function updateProb(id, value) {
    const next = Math.min(100, Math.max(0, parseInt(value, 10) || 0));
    setDeals((state) => state.map((deal) => (deal.dealId === id ? { ...deal, probability: next } : deal)));
    setProbEdit((state) => {
      const copy = { ...state };
      delete copy[id];
      return copy;
    });
  }

  const probByStage = STAGES.map((stage) => {
    const stageDeals = visible.filter((deal) => deal.stage === stage);
    return {
      stage,
      total: stageDeals.reduce((sum, deal) => sum + deal.dealValue, 0),
      weighted: stageDeals.reduce((sum, deal) => sum + deal.dealValue * (deal.probability / 100), 0),
      count: stageDeals.length,
    };
  });

  const maxStage = Math.max(...probByStage.map((stage) => stage.total), 1);

  function TrackKanban({ category, color }) {
    const categoryDeals = visible.filter((deal) => deal.category === category);
    if (!categoryDeals.length) return null;

    const categoryValue = categoryDeals.reduce((sum, deal) => sum + deal.dealValue, 0);
    const categoryWeighted = categoryDeals.reduce((sum, deal) => sum + deal.dealValue * (deal.probability / 100), 0);

    return (
      <div className="pipeline-track">
        <div className="track-banner">
          <span style={{ fontSize: 14, color }}>{category === "New Business" ? "◈" : "◑"}</span>
          <span className="track-label" style={{ color }}>{category}</span>
          <DealCatChipComponent cat={category} />
          <div className="track-divider" style={{ background: `${color}22` }} />
          <span className="track-stat">{categoryDeals.length} deals · {fmtMoney(categoryValue)} · <span style={{ color }}>{fmtMoney(Math.round(categoryWeighted))} weighted</span></span>
        </div>
        <div className="kbn">
          {STAGES.map((stage) => {
            const columnDeals = categoryDeals.filter((deal) => deal.stage === stage);
            return (
              <div className="kbn-col" key={stage}>
                <div className="kbn-head">
                  <div className="kbn-title">{stage}</div>
                  <div className="kbn-meta"><span className="kbn-pill">{columnDeals.length}</span><span className="kbn-pill">{fmtMoney(columnDeals.reduce((sum, deal) => sum + deal.dealValue, 0))}</span></div>
                </div>
                <div className="kbn-body">
                  {columnDeals.length === 0 && <div style={{ textAlign: "center", padding: "22px 0", color: "#253550", fontSize: 12 }}>No deals</div>}
                  {columnDeals.map((deal) => {
                    const age = daysAgo(deal.stageEnteredDate);
                    const stuck = age > 14;
                    const index = STAGES.indexOf(deal.stage);
                    const probVal = probEdit[deal.dealId] !== undefined ? probEdit[deal.dealId] : deal.probability;
                    const probColor = deal.probability >= 70 ? C.green : deal.probability >= 40 ? C.yellow : C.red;
                    return (
                      <div className="deal" key={deal.dealId} onClick={() => nav("deal", deal.dealId)}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 2 }}>
                          <div className="deal-co">{deal.companyName}</div>
                          <div style={{ display: "flex", gap: 4, alignItems: "center" }}>{deal.dealType && <DealTypeBadgeComponent type={deal.dealType} />}{stuck && <span className="bwarn">Stuck</span>}</div>
                        </div>
                        <div className="deal-opp">{deal.opportunityName}</div>
                        <div className="deal-val">{fmtMoney(deal.dealValue)}</div>
                        <div className="prob-row" onClick={(event) => event.stopPropagation()}>
                          <div className="prob-bar-track"><div className="prob-bar-fill" style={{ width: `${deal.probability}%`, background: probColor }} /></div>
                          <input
                            className="prob-input"
                            value={probVal}
                            onChange={(event) => { event.stopPropagation(); setProbEdit((state) => ({ ...state, [deal.dealId]: event.target.value })); }}
                            onBlur={(event) => updateProb(deal.dealId, event.target.value)}
                            onKeyDown={(event) => { if (event.key === "Enter") updateProb(deal.dealId, probEdit[deal.dealId]); }}
                            onClick={(event) => event.stopPropagation()}
                          />
                          <span style={{ fontSize: 10, color: probColor, fontWeight: 700, fontFamily: "monospace" }}>%</span>
                        </div>
                        <div className="prob-weighted">Weighted: {fmtMoney(Math.round(deal.dealValue * (deal.probability / 100)))}</div>
                        <div className="deal-foot">
                          <span style={{ color: deal.assignedConsultant ? C.dim : C.red }}>{deal.assignedConsultant || "⚠ Unassigned"}</span>
                          <span style={{ color: stuck ? C.yellow : C.dim, fontSize: 10.5 }}>{age}d in stage</span>
                        </div>
                        <div className="deal-acts" onClick={(event) => event.stopPropagation()}>
                          {index > 0 && <button className="btn-sm btn-back" onClick={() => move(deal.dealId, -1)}>← Back</button>}
                          {index < STAGES.length - 1 && <button className="btn-sm btn-adv" onClick={() => move(deal.dealId, 1)}>Advance →</button>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="fade">
      <div className="pg-top">
        <div className="pg-title">Pipeline</div>
        <div className="pg-sub">Commercial opportunities · New Business, Expansion &amp; Renewal · Edit win probability inline</div>
      </div>
      <div className="pg-body">
        <div className="krow" style={{ marginBottom: 14 }}>
          <KpiCardComponent label="Pipeline Value" value={fmtMoney(totalVal)} sub={`${visible.length} deals`} />
          <KpiCardComponent label="Weighted Forecast" value={fmtMoney(Math.round(weightedVal))} sub="Prob-adjusted" accent={C.green} />
          <KpiCardComponent label="New Business" value={fmtMoney(byCat("New Business").reduce((sum, deal) => sum + deal.dealValue, 0))} sub={`${byCat("New Business").length} deals`} accent={C.blue} />
          <KpiCardComponent label="Existing Client" value={fmtMoney(byCat("Existing Client").reduce((sum, deal) => sum + deal.dealValue, 0))} sub={`${byCat("Existing Client").length} deals`} accent={C.green} />
        </div>
        <div className="prob-agg-panel">
          <div style={{ fontSize: 10.5, fontWeight: 700, color: C.dim, textTransform: "uppercase", letterSpacing: ".11em", marginBottom: 11 }}>Probability Weighting by Stage</div>
          {probByStage.map(({ stage, total, weighted, count }) => (
            <div className="prob-agg-row" key={stage}>
              <span className="prob-agg-label">{stage}</span>
              <div className="prob-agg-track">
                <div className="prob-agg-total" style={{ width: `${(total / maxStage) * 100}%` }} />
                <div className="prob-agg-weighted" style={{ width: `${(weighted / maxStage) * 100}%` }} />
              </div>
              <span className="prob-agg-val">{fmtMoney(Math.round(weighted))} / {fmtMoney(total)}</span>
              <span style={{ fontSize: 10, color: C.dim, minWidth: 24, textAlign: "right" }}>{count}×</span>
            </div>
          ))}
          <div style={{ fontSize: 11, color: C.dim, marginTop: 9, display: "flex", gap: 14 }}>
            <span style={{ display: "flex", alignItems: "center", gap: 5 }}><span style={{ width: 10, height: 3, background: "#3b7ffa", borderRadius: 2, display: "inline-block" }} /> Weighted</span>
            <span style={{ display: "flex", alignItems: "center", gap: 5 }}><span style={{ width: 10, height: 3, background: "#1c2e52", borderRadius: 2, display: "inline-block" }} /> Total value</span>
          </div>
        </div>
        <div className="fbar">
          <input className="finput" placeholder="Search deals…" value={search} onChange={(event) => setSearch(event.target.value)} style={{ width: 240 }} />
          <select className="fsel" value={ownerF} onChange={(event) => setOwnerF(event.target.value)}>{owners.map((owner) => <option key={owner}>{owner}</option>)}</select>
          <div style={{ display: "flex", gap: 2, background: "#070e20", border: "1px solid #14213d", borderRadius: 8, padding: 3 }}>
            {[["both", "Both Tracks"], ["new", "New Business"], ["existing", "Existing Client"]].map(([value, label]) => (
              <button key={value} onClick={() => setTrackView(value)} style={{ padding: "5px 12px", borderRadius: 6, border: trackView === value ? "1px solid rgba(59,127,250,.28)" : "1px solid transparent", background: trackView === value ? "rgba(59,127,250,.14)" : "none", color: trackView === value ? C.blue : C.dim, cursor: "pointer", fontSize: 12, fontFamily: "'Noto Sans',sans-serif", transition: "all .12s" }}>{label}</button>
            ))}
          </div>
          <div className="finfo" style={{ marginLeft: "auto" }}><strong>{visible.length}</strong> deals · <strong className="mono">{fmtMoney(totalVal)}</strong></div>
        </div>
        {(trackView === "both" || trackView === "new") && <TrackKanban category="New Business" color={C.blue} />}
        {(trackView === "both" || trackView === "existing") && <TrackKanban category="Existing Client" color={C.green} />}
      </div>
    </div>
  );
}
